<template>
  <div
    id="app"
    class="app-wrapper"
  >
    <!-- TOP: Navbar -->
    <Navbar v-if="isLoggedIn" />
    <div class="container-fluid">
      <div class="row">
        <!-- LEFT: sidebar -->
        <Sidebar v-if="isLoggedIn" />
        <!-- Content -->
        <main
          role="main"
          class="main-padding px-md-4 px-2 py-3"
        >
          <!-- animation -->
          <transition
            name="fade-transform"
            mode="out-in"
          >
            <!-- Rendered content -->
            <router-view :key="key" />
          </transition>
        </main>
      </div>
    </div>
  </div>
</template>

<script lang="ts">

import { Component, Vue } from 'vue-property-decorator'
import Navbar from '@/layout/components/Navbar.vue'
import Sidebar from '@/layout/components/Sidebar.vue'
import { UserModule } from '@/store/modules/user'

@Component({
  name: 'Layout',
  components: {
    Navbar,
    Sidebar
  }
})
export default class extends Vue {
  created(): void {
    // If the component's body is too small, the height of the whole app will be small, so multiselect dropdowns will be cut
    window.onresize = function() {
      const appMainElement = document.querySelector('.app-wrapper') as HTMLElement
      if (appMainElement) {
        appMainElement.style.minHeight = window.innerHeight + 'px'
      }
    }
  }

  mounted(): void {
    const appMainElement = document.querySelector('.app-wrapper') as HTMLElement
    if (appMainElement) {
      appMainElement.style.minHeight = window.innerHeight + 'px'
    }
  }

  get key(): string {
    return this.$route.path
  }

  get isLoggedIn(): boolean { return !!UserModule.username }
}
</script>

<style lang="scss">
#app {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #2c3e50;
  overflow: hidden;
}

.border-top { border-top: 1px solid #e5e5e5; }
.border-bottom { border-bottom: 1px solid #e5e5e5; }
table.b-table {
  transition: opacity 0.1s ease-in-out;
}

//
// NAV + SIDEBAR
//
@import "~bootstrap/scss/functions";
@import "~bootstrap/scss/variables";
@import "~bootstrap/scss/mixins";

.main-padding {
  margin-top: 56px;
  width: 100%;
  margin-left: 15rem;
}

.sidebar-fixed-width {
  width: 15rem;
}

/*
 * Sidebar
 */

.sidebar {
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  z-index: 100; /* Behind the navbar */
  padding: 56px 0 0; /* Height of navbar */
  box-shadow: inset -1px 0 0 rgba(0, 0, 0, .1);

  -webkit-transition: margin .25s ease-out;
  -moz-transition: margin .25s ease-out;
  -o-transition: margin .25s ease-out;
  transition: margin .25s ease-out;
}

.version-label {
  position: absolute;
  bottom: 0;
  width: 100%;
}

@include media-breakpoint-down(md) {
  .sidebar :not(.version-label *) {
    font-size: smaller;
  }

  .main-padding {
    width: 100%;
    margin-left: 11rem;
  }

  .sidebar-fixed-width {
    width: 11rem;
  }
}

.sidebar-sticky {
  position: relative;
  top: 0;
  height: calc(100vh - 56px);
  padding-top: .5rem;
  overflow-x: hidden;
  overflow-y: auto; /* Scrollable contents if viewport is shorter than content. */
}

@supports ((position: -webkit-sticky) or (position: sticky)) {
  .sidebar-sticky {
    position: -webkit-sticky;
    position: sticky;
  }
}

.sidebar .nav-link {
  font-weight: 500;
  padding-bottom: 0.25rem;
}

.sidebar .nav-link.active,.nav-link.router-link-active {
  color: #588b8b;
  font-weight: bold;
}

.sidebar .nav-link:hover .feather,
.sidebar .nav-link.active,.nav-link.router-link-active .feather {
  color: inherit;
}

.sidebar-heading {
  font-size: .75rem;
  text-transform: uppercase;
}

/*
 * Navbar
 */

.navbar {
  position: fixed;
  left: 0;
  width: 100%;
}

.navbar-brand {
  padding-top: .75rem;
  padding-bottom: .75rem;
  font-size: 1rem;
  background-color: rgba(0, 0, 0, .25);
  box-shadow: inset -1px 0 0 rgba(0, 0, 0, .25);
}

.navbar .navbar-toggler {
  top: .25rem;
  right: 1rem;
}

.navbar .form-control {
  padding: .75rem 1rem;
  border-width: 0;
  border-radius: 0;
}

.form-control-dark {
  color: #fff;
  background-color: rgba(255, 255, 255, .1);
  border-color: rgba(255, 255, 255, .1);
}

.form-control-dark:focus {
  border-color: transparent;
  box-shadow: 0 0 0 3px rgba(255, 255, 255, .25);
}
</style>
